import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useApiClient } from '../ApiClientProvider';
import { queryKeys } from '../keys';
import type { ContactPerson, ContactPersonPayload } from '../types/pickups';

type Wrapped<T> = { data: T };

export function useContactPersons() {
  const api = useApiClient();
  return useQuery<ContactPerson[], Error>({
    queryKey: queryKeys.contactPersons.list(),
    queryFn: async ({ signal }) => {
      const response = await api.get<Wrapped<ContactPerson[]>>('/admin/contact-persons', signal);
      return response.data;
    },
  });
}

export function useCreateContactPerson() {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation<ContactPerson, Error, ContactPersonPayload>({
    mutationFn: async (payload) => {
      const response = await api.post<Wrapped<ContactPerson>>('/admin/contact-persons', payload);
      return response.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.contactPersons.all });
    },
  });
}

export function useUpdateContactPerson(id: number) {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation<ContactPerson, Error, ContactPersonPayload>({
    mutationFn: async (payload) => {
      const response = await api.put<Wrapped<ContactPerson>>(
        `/admin/contact-persons/${id}`,
        payload,
      );
      return response.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.contactPersons.all });
    },
  });
}

export function useDeleteContactPerson() {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation<void, Error, number>({
    mutationFn: async (id) => {
      await api.del(`/admin/contact-persons/${id}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.contactPersons.all });
    },
  });
}

export function useSetDefaultContactPerson() {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation<ContactPerson, Error, number>({
    mutationFn: async (id) => {
      const response = await api.post<Wrapped<ContactPerson>>(
        `/admin/contact-persons/${id}/set-default`,
      );
      return response.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.contactPersons.all });
    },
  });
}
